// Carrito Jumak: botón "Agregar" de recomendados y mensaje de regalo del carrito lateral.
// Usa los eventos estándar de Horizon para que el carrito se re-renderice (Section Rendering API)
// sin recargar la página.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig, debounce } from '@theme/utilities';

/** @returns {string[]} */
function cartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`cart ${response.status}`);
  return response.json();
}

/**
 * Cantidad de artículos leída del HTML de sección devuelto por /cart/add.js (respaldo si /cart.js falla).
 * @param {Record<string, string> | undefined} sections
 * @returns {number | undefined}
 */
function countFromSections(sections) {
  if (!sections) return undefined;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const value = doc.querySelector('[ref="cartItemCount"]')?.textContent;
    const count = value ? parseInt(value, 10) : NaN;
    if (Number.isFinite(count)) return count;
  }
  return undefined;
}

/** Cambios del carrito en curso (cantidad, quitar, agregar) mientras se resuelven. */
let pendingCartUpdates = 0;

/** @param {boolean} busy */
function setAddButtonsBusy(busy) {
  for (const button of document.querySelectorAll('#cart-drawer [data-jk-add]')) {
    if (!(button instanceof HTMLButtonElement)) continue;
    if (busy) {
      button.disabled = true;
      button.dataset.jkLocked = 'true';
    } else if (button.dataset.jkLocked) {
      button.disabled = false;
      delete button.dataset.jkLocked;
    }
  }
}

// Bloquea "Agregar" mientras otro cambio del carrito (cart/change, cart/add) no ha terminado.
document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
  const promise = /** @type {any} */ (event).promise;
  if (!promise || typeof promise.finally !== 'function') return;
  pendingCartUpdates += 1;
  setAddButtonsBusy(true);
  promise
    .catch(() => {})
    .finally(() => {
      // Deja que el morph termine antes de reactivar los botones nuevos.
      requestAnimationFrame(() => {
        pendingCartUpdates = Math.max(0, pendingCartUpdates - 1);
        if (pendingCartUpdates === 0) setAddButtonsBusy(false);
      });
    });
});

/** Tras el morph, si el foco se perdió, lo devuelve al carrito. */
function restoreFocus() {
  // El morph de la sección es asíncrono: se espera un momento antes de revisar el foco.
  setTimeout(() => {
    const active = document.activeElement;
    if (active && active !== document.body && document.contains(active)) return;
    const next = /** @type {HTMLElement | null} */ (document.querySelector('#cart-drawer [data-jk-add]:not([disabled])'));
    const heading = /** @type {HTMLElement | null} */ (document.getElementById('cart-drawer-heading'));
    const target = heading || next;
    if (!target) return;
    if (target === heading && !heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }, 450);
}

class JkCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    if (pendingCartUpdates > 0) setAddButtonsBusy(true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest('[data-jk-add]'));
    if (!button || button.disabled || pendingCartUpdates > 0) return;
    event.preventDefault();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const status = this.querySelector('[data-jk-status]');
    const errorBox = /** @type {HTMLElement | null} */ (this.querySelector('[data-jk-error]'));
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();
    let settled = false;
    /** @param {any} value */
    const resolve = (value) => {
      settled = true;
      deferred.resolve(value);
    };

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'dialog',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: cartSectionIds().join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();
      const added = response.ok && !data.status;

      /** @type {any} */
      let cart = null;
      try {
        cart = await fetchCart();
      } catch {
        cart = null;
      }

      if (!added) {
        const message = data.description || data.message || 'No se pudo agregar el producto';
        if (cart) {
          resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, itemCount: cart.item_count, source: 'jk-cart-upsell' },
          });
        }
        throw new Error(message);
      }

      if (status) status.textContent = 'Producto agregado al carrito';
      // El producto ya se agregó: aunque /cart.js falle, se resuelve sin error para no invitar a agregarlo dos veces.
      resolve(
        cart
          ? {
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: {
                didError: false,
                items: cart.items,
                itemCount: cart.item_count,
                sections: data.sections,
                source: 'jk-cart-upsell',
              },
            }
          : { detail: { didError: false, sections: data.sections, itemCount: countFromSections(data.sections), source: 'jk-cart-upsell' } }
      );
      restoreFocus();
    } catch (error) {
      if (!settled) deferred.reject(error);
      const message =
        error instanceof Error && !/fetch|network|failed/i.test(error.message)
          ? error.message
          : 'No se pudo agregar el producto. Revisa tu conexión e intenta de nuevo.';
      if (errorBox) {
        errorBox.textContent = message;
        errorBox.hidden = false;
      }
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };
}

class JkGiftNote extends HTMLElement {
  /** @type {AbortController | null} */
  #active = null;

  connectedCallback() {
    this.addEventListener('input', this.#onInput);
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.#onInput);
  }

  /** @param {Event} event */
  #onInput = (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;
    this.#save(event.target.value);
  };

  #save = debounce(async (/** @type {string} */ note) => {
    const status = this.querySelector('[data-jk-note-status]');
    this.#active?.abort();
    const controller = new AbortController();
    this.#active = controller;
    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ note }) }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(String(response.status));
      if (status) status.textContent = note ? 'Mensaje guardado' : '';
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (status) status.textContent = 'No se pudo guardar el mensaje. Intenta de nuevo.';
    } finally {
      if (this.#active === controller) this.#active = null;
    }
  }, 500);
}

if (!customElements.get('jk-cart-upsell')) customElements.define('jk-cart-upsell', JkCartUpsell);
if (!customElements.get('jk-gift-note')) customElements.define('jk-gift-note', JkGiftNote);
